import React, { useState, useEffect } from 'react';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5001';

export default function AdminBlogs() {
    const [blogs, setBlogs] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    
    // UI States
    const [showAdd, setShowAdd] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [toasts, setToasts] = useState<any[]>([]);

    // Form States
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [excerpt, setExcerpt] = useState('');
    const [category, setCategory] = useState('News');
    const [tags, setTags] = useState('');
    const [status, setStatus] = useState('draft');
    const [image, setImage] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);

    const addToast = (message: string, type = 'success') => {
        const id = Date.now() + Math.random();
        setToasts(t => [...t, { id, message, type }]);
        setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3000);
    };

    const fetchBlogs = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE}/api/blogs/admin`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) setBlogs(await res.json());
        } catch (e) {
            console.error('Failed to fetch blogs:', e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchBlogs(); }, []);

    const resetForm = () => {
        setTitle(''); setContent(''); setExcerpt(''); setCategory('News'); setTags(''); setStatus('draft');
        setImage(null); setPreviewUrl(null); setEditingId(null); setShowAdd(false);
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setImage(file);
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!title || !content) {
            addToast('Title and Content are required', 'error');
            return;
        }

        setIsSaving(true);
        try {
            const token = localStorage.getItem('token');
            const formData = new FormData();
            formData.append('title', title);
            formData.append('content', content);
            formData.append('excerpt', excerpt);
            formData.append('category', category);
            formData.append('tags', tags);
            formData.append('status', status);
            if (image) formData.append('image', image);

            const url = editingId ? `${API_BASE}/api/blogs/${editingId}` : `${API_BASE}/api/blogs`;
            const method = editingId ? 'PATCH' : 'POST';

            const res = await fetch(url, {
                method,
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });

            if (res.ok) {
                const data = await res.json();
                if (editingId) {
                    setBlogs(blogs.map(b => b.id === editingId ? data : b));
                    addToast('Blog post updated');
                } else {
                    setBlogs([data, ...blogs]);
                    addToast('Blog post created');
                }
                resetForm();
            } else {
                addToast('Failed to save blog post', 'error');
            }
        } catch (error) {
            addToast('Network error', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const startEdit = (blog: any) => {
        setEditingId(blog.id);
        setTitle(blog.title);
        setContent(blog.content);
        setExcerpt(blog.excerpt || '');
        setCategory(blog.category || 'News');
        setTags(blog.tags || '');
        setStatus(blog.status);
        setPreviewUrl(blog.coverImage);
        setShowAdd(true);
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Delete this blog post?')) return;
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE}/api/blogs/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                setBlogs(blogs.filter(b => b.id !== id));
                addToast('Blog post deleted');
            }
        } catch (error) {
            addToast('Delete failed', 'error');
        }
    };

    if (loading) return <div className="text-center py-12 text-slate-400">Loading blogs...</div>;

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-slate-800">Blog & Content</h2>
                    <p className="text-slate-500 text-sm">Manage articles and educational content</p>
                </div>
                {!showAdd && (
                    <button onClick={() => setShowAdd(true)} className="bg-emerald-700 hover:bg-emerald-800 text-white px-5 py-2.5 rounded-xl font-semibold text-sm transition-all flex items-center gap-2 cursor-pointer shadow-lg shadow-emerald-700/20">
                        <i className="ri-add-line"></i> New Article
                    </button>
                )}
            </div>

            {/* Toasts */}
            <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
                {toasts.map(t => (
                    <div key={t.id} className={`px-5 py-3 rounded-2xl text-sm font-medium flex items-center gap-3 shadow-xl animate-slide-in ${t.type === 'error' ? 'bg-red-50 text-red-700 border border-red-100' : 'bg-white text-emerald-800 border border-emerald-100'}`}>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${t.type === 'error' ? 'bg-red-100' : 'bg-emerald-100'}`}>
                            <i className={t.type === 'error' ? 'ri-error-warning-line' : 'ri-check-line'} />
                        </div>
                        {t.message}
                    </div>
                ))}
            </div>

            {showAdd ? (
                <div className="bg-white rounded-[2rem] border border-slate-100 p-8 shadow-sm">
                    <div className="flex items-center justify-between mb-8">
                        <h3 className="text-lg font-bold text-slate-800">{editingId ? 'Edit Article' : 'Compose New Article'}</h3>
                        <button onClick={resetForm} className="text-slate-400 hover:text-slate-600 p-2 cursor-pointer"><i className="ri-close-fill text-2xl"></i></button>
                    </div>

                    <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        <div className="lg:col-span-2 space-y-6">
                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Title</label>
                                <input required value={title} onChange={e => setTitle(e.target.value)} placeholder="Enter a descriptive title..." className="w-full bg-slate-50 border-none rounded-2xl px-5 py-4 text-sm focus:ring-2 focus:ring-emerald-500/20 outline-none" />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Main Content</label>
                                <textarea required value={content} onChange={e => setContent(e.target.value)} placeholder="Write your article content here..." className="w-full bg-slate-50 border-none rounded-2xl px-5 py-4 text-sm focus:ring-2 focus:ring-emerald-500/20 outline-none h-96 resize-none" />
                            </div>
                        </div>

                        <div className="space-y-6">
                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Cover Image</label>
                                <div className="border-2 border-dashed border-slate-100 rounded-[2rem] p-4 text-center group hover:border-emerald-200 transition-all">
                                    {previewUrl ? (
                                        <div className="relative rounded-2xl overflow-hidden aspect-video">
                                            <img src={previewUrl} alt="Preview" className="w-full h-full object-cover" />
                                            <button type="button" onClick={() => { setImage(null); setPreviewUrl(null); }} className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                                                <i className="ri-close-line"></i>
                                            </button>
                                        </div>
                                    ) : (
                                        <label className="flex flex-col items-center gap-2 py-8 cursor-pointer">
                                            <div className="w-12 h-12 rounded-2xl bg-slate-50 flex items-center justify-center text-slate-400 group-hover:text-emerald-500 group-hover:bg-emerald-50 transition-all">
                                                <i className="ri-image-add-line text-2xl"></i>
                                            </div>
                                            <span className="text-xs font-semibold text-slate-400">Add Cover Image</span>
                                            <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                                        </label>
                                    )}
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Category</label>
                                <select value={category} onChange={e => setCategory(e.target.value)} className="w-full bg-slate-50 border-none rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-emerald-500/20 outline-none">
                                    <option value="Study Tips">Study Tips</option>
                                    <option value="Exam Prep">Exam Prep</option>
                                    <option value="Computer Science">Computer Science</option>
                                    <option value="News">News</option>
                                    <option value="Guides">Guides</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Status</label>
                                <div className="flex gap-2">
                                    {['draft', 'published'].map(s => (
                                        <button key={s} type="button" onClick={() => setStatus(s)} className={`flex-1 py-3 rounded-xl text-xs font-bold capitalize transition-all border ${status === s ? 'bg-slate-900 border-slate-900 text-white' : 'bg-white border-slate-100 text-slate-400 hover:border-slate-200'} cursor-pointer`}>
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Excerpt</label>
                                <textarea value={excerpt} onChange={e => setExcerpt(e.target.value)} placeholder="Brief summary of the article..." className="w-full bg-slate-50 border-none rounded-2xl px-5 py-4 text-sm focus:ring-2 focus:ring-emerald-500/20 outline-none h-32 resize-none" />
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Tags</label>
                                <input value={tags} onChange={e => setTags(e.target.value)} placeholder="e.g. computer science, tutorials" className="w-full bg-slate-50 border-none rounded-2xl px-5 py-4 text-sm focus:ring-2 focus:ring-emerald-500/20 outline-none" />
                            </div>

                            <button type="submit" disabled={isSaving} className="w-full bg-emerald-700 hover:bg-emerald-800 text-white py-4 rounded-2xl font-bold text-sm transition-all shadow-xl shadow-emerald-900/20 disabled:opacity-50 cursor-pointer">
                                {isSaving ? <><i className="ri-loader-4-line animate-spin mr-2"></i> Saving...</> : (editingId ? 'Update Article' : 'Publish Article')}
                            </button>
                        </div>
                    </form>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {blogs.length === 0 ? (
                        <div className="md:col-span-3 text-center py-20 bg-white rounded-[2rem] border border-dashed border-slate-200">
                            <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6">
                                <i className="ri-article-line text-3xl text-slate-200"></i>
                            </div>
                            <h3 className="font-bold text-slate-800 mb-2">No articles yet</h3>
                            <p className="text-slate-400 text-sm">Start sharing knowledge with your students today.</p>
                        </div>
                    ) : (
                        blogs.map(blog => (
                            <div key={blog.id} className="bg-white rounded-[2rem] border border-slate-100 overflow-hidden hover:shadow-xl hover:shadow-slate-200/50 transition-all group flex flex-col">
                                <div className="aspect-video bg-slate-100 relative overflow-hidden">
                                    {blog.coverImage ? (
                                        <img src={blog.coverImage} alt={blog.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-slate-300">
                                            <i className="ri-image-line text-4xl"></i>
                                        </div>
                                    )}
                                    <div className="absolute top-4 right-4 flex gap-2 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all">
                                        <button onClick={() => startEdit(blog)} className="w-9 h-9 rounded-xl bg-white shadow-lg text-slate-600 flex items-center justify-center hover:text-emerald-600 transition-colors cursor-pointer"><i className="ri-edit-line"></i></button>
                                        <button onClick={() => handleDelete(blog.id)} className="w-9 h-9 rounded-xl bg-white shadow-lg text-slate-600 flex items-center justify-center hover:text-red-500 transition-colors cursor-pointer"><i className="ri-delete-bin-line"></i></button>
                                    </div>
                                    <div className={`absolute bottom-4 left-4 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${blog.status === 'published' ? 'bg-emerald-500 text-white' : 'bg-slate-900 text-white'}`}>
                                        {blog.status}
                                    </div>
                                </div>
                                <div className="p-6 flex-1 flex flex-col">
                                    <h4 className="font-bold text-slate-800 mb-2 leading-tight group-hover:text-emerald-700 transition-colors">{blog.title}</h4>
                                    <p className="text-sm text-slate-400 line-clamp-2 mb-4">{blog.excerpt || 'No summary provided.'}</p>
                                    <div className="mt-auto pt-4 border-t border-slate-50 flex items-center justify-between">
                                        <span className="text-[10px] font-bold text-slate-300 uppercase tracking-widest flex items-center gap-1">
                                            <i className="ri-time-line"></i> {new Date(blog.createdAt).toLocaleDateString()}
                                        </span>
                                        <span className="text-[10px] font-bold text-slate-300 uppercase tracking-widest flex items-center gap-1">
                                            <i className="ri-eye-line"></i> {blog.views} Views
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}
